import React, { FunctionComponent, useCallback, useEffect, useReducer, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, CircularLoader, Dropdown, Pill } from 'rs-emd-ui-atoms';
import { useUserDispatch } from '../../../../../components/app/user-context';
import { basketService } from '../../../../../services/basket-service';
import { pricingService } from '../../../../../services/pricing-service';
import { getLabel } from '../../../helpers/html.utils';
import { Validation } from '../../../helpers/validation.utils';
import { StockStatus } from '../../../models/common/stock.model';
import { ConfirmationModal } from '../../shared';
import styles from './compare-alternatives-product-header-component.module.scss';
import {
  Action,
  CompareAlternativesProductHeaderActionType,
  CompareAlternativesProductHeaderProps,
  State,
} from './compare-alternatives-product-header.model';

export const CompareAlternativesProductHeader: FunctionComponent<CompareAlternativesProductHeaderProps> = (
  props: CompareAlternativesProductHeaderProps
) => {
  const userDispatch = useUserDispatch();

  const [state, dispatch] = useReducer(reducer, {
    quantity: props.product.priceArea?.minimumOrderQuantity ?? 1,
    isValidQty: true,
    isBusy: false,
    isDropdownOpen: false,
    defaultDropdownSelection: props.product.priceArea?.minimumOrderQuantity ?? 1,
    prices: [],
    showAddConfirmationModal: false,
  });

  let searchValueTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  //get price for quantity not listed in dropdown values
  useEffect(() => {
    if (state.isSearchingUnlistedQuantity && state.isValidQty) {
      const qtySearched = state.quantity;

      pricingService.getPricesByQty({ stockCode: props.product.title.stockCode, quantity: qtySearched }).then((res) => {
        dispatch({
          type: CompareAlternativesProductHeaderActionType.SetUnlistedQuantityPrice,
          qty: qtySearched,
          price:
            res.priceArea?.pricesExVat && res.priceArea?.pricesExVat.length > 0
              ? res.priceArea.pricesExVat[0].totalPrice.priceAfter?.displayValue
              : res.priceArea?.prices && res.priceArea?.prices.length > 0
              ? res.priceArea.prices[0].totalPrice.priceAfter?.displayValue
              : undefined,
          message: res.priceArea && res.priceArea.errorMessage.length > 0 ? res.priceArea?.errorMessage : undefined,
        });
      });
    } else if (state.unlistedQuantityPrice) {
      //if changing search from an unlisted quantity to another quantity
      dispatch({ type: CompareAlternativesProductHeaderActionType.SetUnlistedQuantityPrice, price: undefined });
    }
  }, [props.product.title.stockCode, state.isSearchingUnlistedQuantity, state.isValidQty, state.quantity, state.unlistedQuantityPrice]);

  //useCallback required to run only when state changes & when fn is called
  const getDropDownValues = useCallback(() => {
    // if errormessage and qty is not a substring of listed value
    if (
      !state.isValidQty &&
      state.prices?.map((p) => p.quantity.toString()).filter((p) => p.includes(state.quantity.toString().toLowerCase())).length === 0
    ) {
      return [];
    } else if ((state.prices ?? []).length > 0) {
      if (!state.isSearchingUnlistedQuantity) {
        return state.prices?.map((p) => {
          return {
            key: p.quantity,
            elt: (
              <div className={styles['qty-opt']}>
                <span>{p.quantity}</span>
                <span className={styles['qty-price']}>{p.totalPrice.priceAfter?.displayValue}</span>
              </div>
            ),
            searchableString: p.quantity.toString(),
          };
        });
      } else {
        return [
          {
            key: state.quantity,
            elt: (
              <div className={styles['qty-opt']}>
                <span>{state.quantity}</span>
                {state.unlistedQuantityPrice ? (
                  <span className={styles['qty-price']}>{state.unlistedQuantityPrice}</span>
                ) : (
                  <CircularLoader />
                )}
              </div>
            ),
            searchableString: state.quantity.toString(),
          },
        ];
      }
    } else {
      return [
        {
          key: props.product.priceArea?.minimumOrderQuantity ?? 1,
          elt: (
            <div className={styles['qty-opt']}>
              <span>{props.product.priceArea?.minimumOrderQuantity}</span>
              <span className={styles['qty-price']}>{props.product.priceArea?.price?.priceAfter?.displayValue}</span>
            </div>
          ),
          searchableString: props.product.priceArea?.minimumOrderQuantity.toString(),
        },
      ];
    }
  }, [
    props.product.priceArea,
    state.isSearchingUnlistedQuantity,
    state.isValidQty,
    state.prices,
    state.quantity,
    state.unlistedQuantityPrice,
  ]);

  return (
    <Card
      squareCorners={true}
      className={`${styles['compare-alternatives-product-module']} ${props.inFixedCTA ? styles['fixed-cta'] : ''} ${
        props.isFirstProduct ? styles['first-product'] : ''
      } ${props.isLastProduct ? styles['last-product'] : ''}`}>
      <Link className={styles['thumb-container']} to={props.product.title.pdpUrl}>
        <img className={styles.thumb} src={props.product.title.primaryImageUrl} alt='product_image'></img>
      </Link>

      <div className={styles['product-info']}>
        <div className={styles['product-div']}>
          <Link className={styles['product-title']} to={props.product.title.pdpUrl}>
            {props.product.title.title}
          </Link>
          <Pill class={`${styles.stock} stock ${props.product.stockArea?.statusCode.toLowerCase()}`}>
            <p dangerouslySetInnerHTML={{ __html: props.product.stockArea?.title ?? '' }} />
          </Pill>
        </div>
        {props.product.stockArea?.statusCode !== StockStatus.Discontinued && props.product.priceArea && (
          <div className={styles['qty-basket-area-wrapper']}>
            <div className={styles['qty-basket-area']}>
              <Dropdown
                onSearchValueChange={onSearchValueChange}
                onFocus={() => {
                  //to reset list especially for mobile modal
                  if (state.isSearchingUnlistedQuantity) {
                    dispatch({
                      type: CompareAlternativesProductHeaderActionType.SearchListedQuantity,
                    });
                  }
                }}
                isOpen={state.isDropdownOpen}
                defaultSelection={state.defaultDropdownSelection}
                className={styles.qty}
                isSearchable={true}
                customSearchHeader={mobileSearchHeader()} // TODO: check if need in compare alt
                searchableInputType='number'
                additionalInfo={`${getLabel(props.labels, 'available_in_multiples')} ${props.product.priceArea?.ssm}`}
                list={getDropDownValues()}
                isValid={state.isValidQty}
                validationCallback={() => state.isValidQty}
                searchableStepValue={props.product.priceArea?.ssm}
                minNumericValue={props.product.priceArea?.minimumOrderQuantity ?? 1}
                selectOnClickOutside={true}
                onChangeState={(isOpen: boolean) => onPricesDropdownToggle(isOpen)}
              />
              <Button
                text={!state.isBusy ? getLabel(props.labels, 'add_to_basket') : getLabel(props.labels, 'adding_to_basket')}
                className={styles['add']}
                disabled={!state.isValidQty || state.isBusy}
                onClick={() => onAddToBasketClick(props.product.title.stockCode)}
              />
              <Button
                icon={!state.isBusy ? 'basket-add' : 'circular-loader'}
                className={styles['add-icon']}
                title={getLabel(props.labels, 'add_to_basket')}
                disabled={!state.isValidQty || state.isBusy}
                onClick={() => onAddToBasketClick(props.product.title.stockCode)}
              />
            </div>
            {(state.errorMsg ?? '').length > 0 && <p className={styles.error}>{state.errorMsg}</p>}
          </div>
        )}
      </div>

      {state.showAddConfirmationModal && (
        <ConfirmationModal
          labels={props.labels}
          confirmationModalTitle={getLabel(props.labels, 'added_to_basket')}
          title={props.product.title.title}
          details={
            <p className={`snippet ${styles.quantity}`}>
              {getLabel(props.labels, 'quantity_added').replace('[quantity]', state.quantity.toString())}
            </p>
          }
          imageUrl={props.product.title.primaryImageUrl}
          viewButtonHref={'/basket'}
          viewButtonText={getLabel(props.labels, 'view_basket')}
          backButtonText='continue_shopping'
          closeCallback={() => dispatch({ type: CompareAlternativesProductHeaderActionType.CloseAddConfirmationModal })}
        />
      )}
    </Card>
  );

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case CompareAlternativesProductHeaderActionType.IsBusy: {
        return {
          ...state,
          isBusy: action.isBusy,
          showAddConfirmationModal: !action.isBusy && (action.isSuccess ?? false),
          isDropdownOpen: action.isDropdownOpen !== undefined ? action.isDropdownOpen : state.isDropdownOpen,
        };
      }
      case CompareAlternativesProductHeaderActionType.SetPriceData: {
        return {
          ...state,
          prices: action.data.productPrices ?? [],
        };
      }
      case CompareAlternativesProductHeaderActionType.ToggleDropDownOpen: {
        return {
          ...state,
          isDropdownOpen: action.isOpen,
          isSearchingUnlistedQuantity: false,
          defaultDropdownSelection: undefined,
        };
      }
      case CompareAlternativesProductHeaderActionType.SetUnlistedQuantityPrice: {
        let isValid = !(action.message && action.message !== '');
        const doesQtyMatchSearched = (action.qty ?? state.quantity) === state.quantity;

        return {
          ...state,
          unlistedQuantityPrice: doesQtyMatchSearched ? action.price : undefined,
          isValidQty: doesQtyMatchSearched ? isValid : state.isValidQty,
          errorMsg: doesQtyMatchSearched ? action.message : state.errorMsg,
          defaultDropdownSelection: doesQtyMatchSearched
            ? isValid
              ? state.defaultDropdownSelection
              : undefined //else it defaults to set value
            : state.defaultDropdownSelection,
        };
      }
      case CompareAlternativesProductHeaderActionType.SetInvalidUnlistedQty: {
        return {
          ...state,
          quantity: action.quantity,
          defaultDropdownSelection: undefined,
          unlistedQuantityPrice: undefined,
          isValidQty: false,
          errorMsg: props.product.priceArea?.qtyErrors?.find((e) => e.errorCode === action.errorCode)?.errorMessage,
        };
      }
      case CompareAlternativesProductHeaderActionType.SearchUnlistedQuantity: {
        return {
          ...state,
          quantity: action.quantity,
          isValidQty: true, // temporarily set to true, pending query result
          isSearchingUnlistedQuantity: true,
          defaultDropdownSelection: undefined,
        };
      }
      case CompareAlternativesProductHeaderActionType.SearchListedQuantity: {
        return {
          ...state,
          quantity: action.quantity ?? state.quantity,
          isSearchingUnlistedQuantity: false,
          defaultDropdownSelection: undefined, //else it defaults to set value
          isValidQty: action.quantity ? true : state.isValidQty,
          errorMsg: action.quantity ? '' : state.errorMsg,
        };
      }
      case CompareAlternativesProductHeaderActionType.CloseAddConfirmationModal: {
        return {
          ...state,
          showAddConfirmationModal: false,
        };
      }
    }
  }

  function onPricesDropdownToggle(isOpen: boolean) {
    dispatch({ type: CompareAlternativesProductHeaderActionType.ToggleDropDownOpen, isOpen: isOpen });

    if (isOpen && !state.prices?.length) {
      pricingService
        .getPrices({ stockCode: props.product.title.stockCode })
        .then((res) => dispatch({ type: CompareAlternativesProductHeaderActionType.SetPriceData, data: res }));
    }
  }

  function onSearchValueChange(value: string) {
    let numVal = Number(value);

    if (searchValueTimeoutRef.current) clearTimeout(searchValueTimeoutRef.current);

    let validationRes = Validation.validateQuantity(
      props.product.priceArea?.minimumOrderQuantity ?? 1,
      props.product.priceArea?.ssm ?? 1,
      value
    );

    //if unlisted quantity
    if (state.prices?.map((p) => p.quantity.toString()).filter((p) => p === value.toLowerCase()).length === 0) {
      if (validationRes.isValid) {
        //throttle 300ms
        searchValueTimeoutRef.current = setTimeout(() => {
          dispatch({
            type: CompareAlternativesProductHeaderActionType.SearchUnlistedQuantity,
            quantity: numVal,
          });
        }, 300);
      } else {
        dispatch({
          type: CompareAlternativesProductHeaderActionType.SetInvalidUnlistedQty,
          quantity: numVal,
          errorCode: validationRes.errorCode,
        });
      }
    }
    //if listed quantity
    else {
      dispatch({
        type: CompareAlternativesProductHeaderActionType.SearchListedQuantity,
        quantity: numVal,
      });
    }
  }

  function onAddToBasketClick(stockCode: string) {
    if (state.isValidQty) {
      dispatch({ type: CompareAlternativesProductHeaderActionType.IsBusy, isBusy: true });
      basketService.addToBasket({ variables: { stockCode, quantity: state.quantity }, actionType: 'tn_page' }, userDispatch).then((res) =>
        dispatch({
          type: CompareAlternativesProductHeaderActionType.IsBusy,
          isBusy: false,
          isSuccess: res.data?.addToBasketV2.isSuccess,
          isDropdownOpen: false,
        })
      );
    }
  }

  function mobileSearchHeader() {
    return (
      <>
        {(state.errorMsg ?? '').length > 0 && <p className={styles['modal-search-header-error']}>{state.errorMsg}</p>}
        <Button
          icon={!state.isBusy ? 'basket-add' : 'circular-loader'}
          title={getLabel(props.labels, 'add_to_basket')}
          disabled={!state.isValidQty || state.isBusy}
          onClick={() => onAddToBasketClick(props.product.title.stockCode)}
        />
      </>
    );
  }
};
